import { FC, useContext, useMemo, useState } from "react";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { getClaimsVolumeDefinitions, getCareJourneyDefinitions, getEfficiencyAndEffectivenessDefinitions, getMotiveScoreDefinitions, getNewPatientClaimsDefinitions } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { valueOrDashes } from "@app/shared/utility/valueOrDashes";
import { PciDataPerLOB } from "@app/projects/network-assessment/provider-hub/interfaces";
import { displayFraction, EMPTY_DISPLAY, formatNewPatientIndicator } from "@app/projects/utility";
import { NPF } from "@app/projects/servicing-provider/types";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";

const defaultPciLobOptions: ClaimsActivityLineOfBusinessTypeEnum[] = [
  ClaimsActivityLineOfBusinessTypeEnum.Commercial,
  ClaimsActivityLineOfBusinessTypeEnum.Medicaid,
  ClaimsActivityLineOfBusinessTypeEnum.Medicare,
  ClaimsActivityLineOfBusinessTypeEnum.Total
];

export const ProviderHubPCIAccordion: FC = () => {

  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const { selectedProvider } = providerHubContext;

  const showVaClaims = !!projectContext?.runConfiguration?.showVaClaimsVolumeColumn;
  const showTricareClaims = !!projectContext?.runConfiguration?.showTricareClaimsVolumeColumn;
  const showMotiveScores = !!projectContext?.runConfiguration?.showMotiveAppropriatenessScore;
  const showCareJourneyScores = !!projectContext?.runConfiguration?.showCareJourneyScores;
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum>(ClaimsActivityLineOfBusinessTypeEnum.Total);

  const lineOfBusinessOptions = useMemo<ClaimsActivityLineOfBusinessTypeEnum[]>(() => {
    const lobOptions = [...defaultPciLobOptions];

    if (showVaClaims) lobOptions.push(ClaimsActivityLineOfBusinessTypeEnum.VA);
    if (showTricareClaims) lobOptions.push(ClaimsActivityLineOfBusinessTypeEnum.TRICARE);
    return lobOptions;
  }, [showVaClaims, showTricareClaims]);

  const pciDataPerLOB =  useMemo<PciDataPerLOB | null>(() => {

    if (!selectedProvider) return null;

    switch (selectedLineOfBusiness) {
      case ClaimsActivityLineOfBusinessTypeEnum.Commercial:
        return {
          claimAmount: selectedProvider.commercialClaimAmount?.toString() ?? EMPTY_DISPLAY,
          claimsVolume: selectedProvider.commercialClaimsVolume ?? EMPTY_DISPLAY,
          newPatientIndicator: selectedProvider.commercialNewPatientInd,
          participation: selectedProvider.commercialParticipation ?? [],
          total: "Commercial ($)",
        };

      case ClaimsActivityLineOfBusinessTypeEnum.Medicaid:
        return {
          claimAmount: selectedProvider.medicaidClaimAmount?.toString() ?? EMPTY_DISPLAY,
          claimsVolume: selectedProvider.medicaidClaimsVolume ?? EMPTY_DISPLAY,
          newPatientIndicator: selectedProvider.medicaidNewPatientInd,
          participation: selectedProvider.medicaidParticipation ?? [],
          total: "Medicaid ($)",
        };

      case ClaimsActivityLineOfBusinessTypeEnum.Medicare:
        return {
          claimAmount: selectedProvider.medicareClaimAmount?.toString() ?? EMPTY_DISPLAY,
          claimsVolume: selectedProvider.medicareClaimsVolume ?? EMPTY_DISPLAY,
          newPatientIndicator: selectedProvider.medicareNewPatientInd,
          participation: selectedProvider.medicareParticipation ?? [],
          total: "Medicare ($)",
        };

      case ClaimsActivityLineOfBusinessTypeEnum.TRICARE:
        return {
          claimAmount: selectedProvider.tricareClaimAmount?.toString() ?? EMPTY_DISPLAY,
          claimsVolume: selectedProvider.tricareClaimsVolume ?? EMPTY_DISPLAY,
          newPatientIndicator: selectedProvider.tricareNewPatientInd,
          participation: selectedProvider.tricareParticipation ?? [],
          total: "Tricare ($)",
        };

      case ClaimsActivityLineOfBusinessTypeEnum.VA:
        return {
          claimAmount: selectedProvider.vaClaimAmount?.toString() ?? EMPTY_DISPLAY,
          claimsVolume: selectedProvider.vaClaimsVolume ?? EMPTY_DISPLAY,
          newPatientIndicator: selectedProvider.vaNewPatientInd,
          participation: selectedProvider.vaParticipation ?? [],
          total: "VA ($)",
        };

      case ClaimsActivityLineOfBusinessTypeEnum.Total:
      default:
        return {
          claimAmount: selectedProvider.totalClaimAmount?.toString() ?? EMPTY_DISPLAY,
          claimsVolume: selectedProvider.totalClaimsVolume ?? EMPTY_DISPLAY,
          newPatientIndicator: selectedProvider.totalNewPatientInd,
          participation: selectedProvider.totalParticipation ?? [],
          total: "Total ($)",
        };
    }
  }, [selectedLineOfBusiness, selectedProvider]);

  const topConditions = selectedProvider?.topConditions ?? [];
  const topProcedures = selectedProvider?.topProcedures ?? [];
  const topPrescriptions = selectedProvider?.topPrescriptions ?? [];
  const motiveScores = selectedProvider?.motiveScores ?? [];
  const careJourneyEpisodes = selectedProvider?.careJourneyScoresTopEpisodes ?? [];

  // Sort participation by claimsVolume descending
  const sortedParticipation = useMemo(() => {
    return [...(pciDataPerLOB?.participation ?? [])].sort((a, b) => (b.claimsVolume ?? 0) - (a.claimsVolume ?? 0));
  }, [pciDataPerLOB?.participation]);


  return (
    <div className="provider-claims-insights">
      <Accordion
        title="Provider Claims Insights"
        defaultOpen={true}
        className="provider-claims-insights__accordion"
        testId="provider-claims-insights__main-accordion"
      >
        <dl className="provider-claims-insights__dl">
          <div className="provider-claims-insights__row">
            <dt className="provider-claims-insights__label">Effectiveness (Quality)
              <TooltipWrapper text={getEfficiencyAndEffectivenessDefinitions(NPF.EffectivenessRank)} position="right" tooltipWidth="400px">
                <div className="waffles-tooltip-indicator">
                  <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" testId="provider-claims-insights__effectiveness-rank-tooltip-info-icon"/>
                </div>
              </TooltipWrapper>
            </dt>
            <dd className="provider-claims-insights__value">{displayFraction(selectedProvider.effectivenessRank, 5)}</dd>
          </div>

           <div className="provider-claims-insights__row">
            <dt className="provider-claims-insights__label">Efficiency (Cost)
              <TooltipWrapper text={getEfficiencyAndEffectivenessDefinitions(NPF.EfficiencyRank)} position="right" tooltipWidth="400px">
                <div className="waffles-tooltip-indicator">
                  <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" testId="provider-claims-insights__efficiency-rank-tooltip-info-icon"/>
                </div>
              </TooltipWrapper>
            </dt>
            <dd className="provider-claims-insights__value">{displayFraction(selectedProvider.efficiencyRank, 5)}</dd>
          </div>

          <AccordionContainer className="provider-claims-insights__nested-accordion-container">
            <Accordion
              title="Top Conditions"
              defaultOpen={false}
              className="provider-claims-insights__subaccordion"
              testId="provider-claims-insights__top_conditions-subaccordion"
            >
               <dl className="provider-claims-insights__dl provider-claims-insights__dl--list">
                {topConditions.length === 0 ? (
                  <div className="provider-claims-insights__row provider-claims-insights__row--empty">
                    {EMPTY_DISPLAY}
                  </div>
                ) : (
                  topConditions.map((condition, index) => (
                    <div key={`${condition}-${index}`} className="provider-claims-insights__row provider-claims-insights__row--indented">
                      {condition || EMPTY_DISPLAY}
                    </div>
                  ))
                )}
               </dl>
              </Accordion>

            <Accordion
              title="Top Procedures"
              defaultOpen={false}
              className="provider-claims-insights__subaccordion"
              testId="provider-claims-insights__top_procedures-subaccordion"
            >
               <dl className="provider-claims-insights__dl provider-claims-insights__dl--list">
                {topProcedures.length === 0 ? (
                  <div className="provider-claims-insights__row provider-claims-insights__row--empty">
                    {EMPTY_DISPLAY}
                  </div>
                ) : (
                  topProcedures.map((procedure, index) => (
                    <div key={`${procedure}-${index}`} className="provider-claims-insights__row provider-claims-insights__row--indented">
                      {procedure || EMPTY_DISPLAY}
                    </div>
                  ))
                )}
              </dl>
            </Accordion>

            <Accordion
              title="Top Prescriptions"
              defaultOpen={false}
              className="provider-claims-insights__subaccordion"
              testId="provider-claims-insights__top_prescriptions-subaccordion"
            >
               <dl className="provider-claims-insights__dl provider-claims-insights__dl--list">
                {topPrescriptions.length === 0 ? (
                  <div className="provider-claims-insights__row provider-claims-insights__row--empty">
                    {EMPTY_DISPLAY}
                  </div>
                ) : (
                  topPrescriptions.map((prescription, index) => (
                    <div key={`${prescription}-${index}`} className="provider-claims-insights__row provider-claims-insights__row--indented">
                      {prescription || EMPTY_DISPLAY}
                    </div>
                  ))
                )}
              </dl>
            </Accordion>

          </AccordionContainer>

          {/* Line of Business sub-section  */}
          <div className="provider-claims-insights__lob">

            <QESSelect
              allowEmpty={false}
              className="provider-claims-insights__lob-select"
              testId="provider-claims-insights__lob-select"
              label="Lines of Business"
              onChange={(e) => setSelectedLineOfBusiness(e.target.value as ClaimsActivityLineOfBusinessTypeEnum)}
              value={selectedLineOfBusiness}
            >
              {lineOfBusinessOptions
                .map((option, optionIndex) => (
                  <option
                    key={optionIndex}
                    value={option}>
                    {option}
                  </option>
                ))}
            </QESSelect>

            {/* LOB Section 1: Claims Volume, Amount, New Patient CPT, Ghost Provider  */}
            <dl className="provider-claims-insights__panel provider-claims-insights__dl">
              <div className="provider-claims-insights__row">
                <dt className="provider-claims-insights__label">Claims Volume
                  <TooltipWrapper text={getClaimsVolumeDefinitions()} position="right">
                    <div className="waffles-tooltip-indicator">
                      <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" testId="provider-claims-insights__claims-volume-tooltip-info-icon"/>
                    </div>
                  </TooltipWrapper>
                </dt>
                <dd className="provider-claims-insights__value">{pciDataPerLOB?.claimsVolume}</dd>
              </div>

              <div className="provider-claims-insights__row">
                <dt className="provider-claims-insights__label">{pciDataPerLOB?.total}
                  <TooltipWrapper text="Total dollar amount spent." position="right">
                    <div className="waffles-tooltip-indicator">
                      <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" testId="provider-claims-insights__total-dollar-amount-tooltip-info-icon"/>
                    </div>
                  </TooltipWrapper>
                </dt>
                <dd className="provider-claims-insights__value">{valueOrDashes(Number(pciDataPerLOB?.claimAmount), true)}</dd>
              </div>

               <div className="provider-claims-insights__row">
                <dt className="provider-claims-insights__label">
                  New Patient CPT
                  <TooltipWrapper text={getNewPatientClaimsDefinitions()} position="right">
                    <div className="waffles-tooltip-indicator">
                      <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" testId="provider-claims-insights__new-patient-cpt-tooltip-info-icon"/>
                    </div>
                  </TooltipWrapper>
                </dt>
                <dd className="provider-claims-insights__value">{formatNewPatientIndicator(pciDataPerLOB?.newPatientIndicator ?? null)}</dd>
              </div>

              <div className="provider-claims-insights__row">
                <dt className="provider-claims-insights__label">Ghost Provider</dt>
                <dd className="provider-claims-insights__value">
                  {pciDataPerLOB?.claimsVolume === ClaimsActivityVolumeLabelEnum.Ghost ? "Yes" : "No"}
                </dd>
              </div>
            </dl>

            {/* LOB Section 2 - Payer Participation (Payer and Total Percent) */}
            <div className="provider-claims-insights__payer-mix">

              <div className="provider-claims-insights__payer-mix-header">
                <div className="provider-claims-insights__payer-mix-header-label">Payer Mix</div>
                <div className="provider-claims-insights__payer-mix-header-value">Total Percent</div>
              </div>

              <dl className="provider-claims-insights__payer-mix-body provider-claims-insights__dl provider-claims-insights__dl--payer-mix">
                {sortedParticipation.length === 0 ? (
                  <div className="provider-claims-insights__row provider-claims-insights__row--payer">
                    <dt className="provider-claims-insights__label provider-claims-insights__label--payer">{EMPTY_DISPLAY}</dt>
                    <dd className="provider-claims-insights__value provider-claims-insights__value--payer">{EMPTY_DISPLAY}</dd>
                  </div>
                ) : (
                  sortedParticipation.map((r: { payerName?: string; claimsVolume?: number }, index: number) => (
                    <div key={`${r.payerName ?? "payer"}-${index}`} className="provider-claims-insights__row provider-claims-insights__row--payer">
                      <dt className="provider-claims-insights__label provider-claims-insights__label--payer">{r.payerName ?? EMPTY_DISPLAY}</dt>
                      <dd className="provider-claims-insights__value provider-claims-insights__value--payer">{((r.claimsVolume ?? 0) as number).toFixed(1)}%</dd>
                    </div>
                  ))
                )}
              </dl>

            </div>
                    
          </div>
          
          {showMotiveScores && (
            <AccordionContainer className="provider-claims-insights__nested-accordion-container">
              <Accordion
                title={
                  <>
                    <TooltipWrapper text={getMotiveScoreDefinitions()} position="right" tooltipWidth="400px">
                    <div className="waffles-tooltip-indicator">
                      <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" testId="provider-claims-insights__motive-appropriateness-tooltip_info_icon"/>
                    </div>
                    </TooltipWrapper>
                    <span>Motive Appropriateness Tiers</span>
                  </>
                }
                defaultOpen={false}
                className="provider-claims-insights__subaccordion"
                testId="provider-claims-insights__motive-appropriateness-subaccordion"
              >
                <dl>
                  {motiveScores.length === 0 ? (
                    <div className="provider-claims-insights__row provider-claims-insights__row--empty">
                      {EMPTY_DISPLAY}
                    </div>
                  ) : (
                    motiveScores.map((score, index) => (
                      <div key={`${score.domainDescription}-${index}`} className="provider-claims-insights__row provider-claims-insights__row--motive">
                        <dt className="provider-claims-insights__label">{score.domainDescription}</dt>
                        <dd className="provider-claims-insights__value">{score.starValue}</dd>
                      </div>
                    ))
                  )}
                </dl>
              </Accordion>
    
            </AccordionContainer>
          )}

          {showCareJourneyScores && (
            <AccordionContainer className="provider-claims-insights__nested-accordion-container">
              <Accordion
                title={
                    <><span>CareJourney Provider Performance</span><img src={`${window.customUIBaseUrl}/images/carejourney-logo.svg`} alt="Arcadia CareJourney" width="120" height="30" /></>
                }
                defaultOpen={false}
                className="provider-claims-insights__subaccordion"
                testId="provider-claims-insights__carejourney-subaccordion"
              >
                {selectedProvider?.careJourneyScoresCostScore == null && selectedProvider?.careJourneyScoresQualityScore == null && careJourneyEpisodes.length === 0 ? (
                  <div className="provider-claims-insights__row provider-claims-insights__row--empty">
                    {EMPTY_DISPLAY}
                  </div>
                ) : (
                  <>
                    <dl className="provider-claims-insights__dl">
                      <div className="provider-claims-insights__row">
                        <dt className="provider-claims-insights__label">
                          Cost Score
                          <TooltipWrapper text={getCareJourneyDefinitions()} position="right" tooltipWidth="300px">
                            <div className="waffles-tooltip-indicator">
                              <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" testId="provider-claims-insights__carejourney-cost-tooltip-info-icon"/>
                            </div>
                          </TooltipWrapper>
                        </dt>
                        <dd className="provider-claims-insights__value">{displayFraction(selectedProvider?.careJourneyScoresCostScore, 5)}</dd>
                      </div>

                      <div className="provider-claims-insights__row">
                        <dt className="provider-claims-insights__label">
                          Quality Score
                          <TooltipWrapper text={getCareJourneyDefinitions()} position="right" tooltipWidth="300px">
                            <div className="waffles-tooltip-indicator">
                              <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" testId="provider-claims-insights__carejourney-quality-tooltip-info-icon"/>
                            </div>
                          </TooltipWrapper>
                        </dt>
                        <dd className="provider-claims-insights__value">{displayFraction(selectedProvider?.careJourneyScoresQualityScore, 5)}</dd>
                      </div>
                    </dl>

                    {careJourneyEpisodes.length > 0 && (
                      <div className="provider-claims-insights__carejourney-episodes">
                        <div className="provider-claims-insights__carejourney-episodes-title">
                          Episode Cost and Quality
                        </div>
                        <table className="provider-claims-insights__carejourney-episodes-table">
                          <thead>
                            <tr>
                              <th>Episode Name &amp; Number</th>
                              <th>Cost</th>
                              <th>Quality</th>
                            </tr>
                          </thead>
                          <tbody>
                            {careJourneyEpisodes.map((episode, index) => (
                              <tr key={`${episode.name}-${index}`}>
                                <td>{episode.name} ({episode.count ?? 0})</td>
                                <td>{displayFraction(episode.costScore, 5)}</td>
                                <td>{displayFraction(episode.qualityScore, 5)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </Accordion>
            </AccordionContainer>
          )}

       </dl>
      </Accordion>
    </div>
  )
};